import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useNewSupportCall from '../../hooks/useNewSupportCall';
import type { FetchNewSupportCallProps, FormFields } from '../../models/newSupportCall';
import moment from 'moment';
import Notification from '@rio-cloud/rio-uikit/Notification';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import AdministrativeForm from './forms/administrative';
import TechnicalForm from './forms/technical';
import CancellationForm from './forms/cancellation';
import { uploadFileSupport } from '../../api/tas';
import SimpleUpload from "../support/components/SimpleUpload";
import { useSelector } from 'react-redux';
import { getUserProfile, type UserProfile } from '../../configuration/login/loginSlice';
import { fetchUser } from '../../api/fetchUser';
import { F2_FORM_SUBJECTS, optionsAdministrative, optionsFinancial, optionsTechnical, optionsVolksTotalPortal, supportStatusOptions, supportSubjectOptions, SupportType } from '../support/constants';
import TasF1Form from './forms/tas/f1';
import TasF2Form from './forms/tas/f2';
import TasF3Form from './forms/tas/f3';
import TasF4Form from './forms/tas/f4';
import TasF5Form from './forms/tas/f5';
import TasF6Form from './forms/tas/f6';
import TasF7Form from './forms/tas/f7';
import VehicleData from '../support/pages/details/components/VehiclesData';

const initialFormData: FormFields = {
    supportType: 'VOLKSTOTAL',
    subject: 'ADMINISTRATIVE',
    subtopic: 'MANUAL_CONTRACT_REGISTRATION',
    responsible: '',
    chassis: '',
    licensePlate: '',
    dn: '',
    createdBy: '',
    customer: '',
    hourMeter: '',
    odometer: '',
    entryDate: '',
    regionalId: '',
    dnResponsible: '',
    phone: '',
    occurrenceOriginId: '',
    details: '',
    partName: '',
    aeg: '',
    customerComment: '',
    diagnose: '',
    repair: '',
    analysisReason: '',
    contractNumber: '',
    vehicleQtd: '',
    cancelReason: '',
    cancelComment: '',
    assignedTo: '',
    status: '',
    createdAt: '',
    updatedAt: '',
};

const NewSupportCallPage = () => {
    const navigate = useNavigate();
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const userProfile = useSelector(getUserProfile);
    const [loggedUserName, setLoggedUserName] = useState("");

    const { 
        role,
        vehicle,
        isLoadingVehicleFetch,
        fetchVehicle,
        createSupportCall
    } = useNewSupportCall();
    
    const [formData, setFormData] = useState<FormFields>(initialFormData);
	const [errors, setErrors] = useState<Partial<Record<keyof FormFields, string>>>({});

    const getUserData = async () => {
        if (!userProfile || !userProfile.sub) return;

        const userId = userProfile.sub.split(':')[1];
        const response = await fetchUser(userId);
        if (!response) return;

        const fullName = (response.firstName ?? '') + ' ' + (response.lastName ?? '');
        setLoggedUserName(fullName);
        setFormData(previous => {
            if (previous.responsible === fullName) return previous;
            return { ...previous, responsible: fullName };
        });
    }

    useEffect(() => {
        getUserData();
    }, [userProfile]);

    const handleBackClick = () => {
        navigate(-1);
    };

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof FormFields, string>> = {};

		if (!loggedUserName && !formData?.responsible?.trim()) {
			newErrors.responsible = 'Responsável é obrigatório';
		}

		if (!formData?.dn?.trim()) {
			newErrors.dn = 'DN é obrigatório';
		}

        if (!formData.phone) {
            newErrors.phone = 'Telefone é obrigatório';
        }
        
        if (subject === 'ADMINISTRATIVE') {
            if(formData?.chassis) {
                if(
                    formData?.chassis?.length < 17 ||
                    formData?.chassis?.length > 17
                ) {
                    newErrors.chassis = 'Chassi do veículo deve ter 17 caracteres';
                }
            }

            if (!formData.regionalId) {
                newErrors.regionalId = 'Regional é obrigatório';
            }
        }

        if (subject === 'TECHNICAL') {
            if(
                !formData?.chassis ||
                formData?.chassis?.length < 17 ||
                formData?.chassis?.length > 17
            ) {
                newErrors.chassis = 'Chassi do veículo deve ter 17 caracteres';
            }

            if (!formData.regionalId) {
                newErrors.regionalId = 'Regional é obrigatório';
            }

            if (!formData.dnResponsible) {
                newErrors.dnResponsible = 'Responsável pelo chamado no DN é obrigatório';
            }
    
            if (!formData.phone) {
                newErrors.phone = 'Telefone é obrigatório';
            }

            if (!formData.partName) {
                newErrors.partName = 'Nome da peça causadora é obrigatório';
            }

            if (!formData.customerComment) {
                newErrors.customerComment = 'Reclamação do cliente é obrigatório';
            }

            if (!formData.diagnose) {
                newErrors.diagnose = 'Diagnóstico / causa da falha é obrigatório';
            }

            if (!formData.repair) {
                newErrors.repair = 'Reparo necessário é obrigatório';
            }

            if (!formData.analysisReason) {
                newErrors.analysisReason = 'Motivo do envio para análise é obrigatório';
            }

            if (!formData.licensePlate) {
                newErrors.licensePlate = 'Placa do veículo é obrigatório';
            }

            if (!formData.customer) {
                newErrors.customer = 'Nome do cliente é obrigatório';
            }

            if (!formData.hourMeter) {
                newErrors.hourMeter = 'Horímetro é obrigatório';
            }

            if (!formData?.odometer) {
                newErrors.odometer = 'Km é obrigatório';
            }

            if (!formData.entryDate) {
                newErrors.entryDate = 'Data de entrada é obrigatória';
            }
        }
        
        if (subject === 'CONTRACT_CANCELLATION') {
            if(formData?.chassis) {
                if(
                    formData?.chassis?.length < 17 ||
                    formData?.chassis?.length > 17
                ) {
                    newErrors.chassis = 'Chassi do veículo deve ter 17 caracteres';
                }
            }

            if (!formData.contractNumber) {
                newErrors.contractNumber = 'Número do contrato é obrigatório';
            }

            if (!formData.vehicleQtd) {
                newErrors.vehicleQtd = 'Quantidade de veículos é obrigatório';
            }

            if (!formData.cancelReason) {
                newErrors.cancelReason = 'Motivo do cancelamento é obrigatório';
            }

            if (!formData.cancelComment) {
                newErrors.cancelComment = 'Solicitação é obrigatório';
            }
        }


        if(
            F2_FORM_SUBJECTS.includes(subject as string) || 
            subject === 'CABIN' ||
            subject === 'EXHAUST_SYSTEM_AND_AFTER_TREATMENT' ||
            subject === 'SOFTWARE_DIESEL_VEHICLE' ||
            subject === 'ENGINE_REPLACEMENT_REQUEST' ||
            subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN' ||
            subject === 'TAS_DIAGNOSIS_TOOLS'
        ) {
            if(formData?.chassis) {
                if(
                    formData?.chassis?.length < 17 ||
                    formData?.chassis?.length > 17
                ) {
                    newErrors.chassis = 'Chassi do veículo deve ter 17 caracteres';
                }
            }
            
            if (!formData.regionalId) {
                newErrors.regionalId = 'Regional é obrigatório';
            }
            
        }

 
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

    const updateField = <K extends keyof FormFields>(field: K, value: FormFields[K]) => {
        setFormData(previousValue => ({
            ...previousValue,
            [field]: value,
        }));
    };

    const updateSupportType = (value: string) => updateField('supportType', value);
    const updateSubject = (value: string) => updateField('subject', value);
    const updateSubtopic = (value: string) => updateField('subtopic', value);

    const handleNewData = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

		if (!validateForm()) return;

        const groupedValues = { ...formData };

        const payload: FetchNewSupportCallProps = {
            supportType: groupedValues.supportType,
            responsible: loggedUserName || groupedValues.responsible,
            chassis: groupedValues.chassis,
            licensePlate: groupedValues.licensePlate,
            dn: groupedValues.dn,
            dnResponsible: groupedValues.responsible,
            customer: groupedValues.customer,
            hourMeter: Number(groupedValues.hourMeter) || undefined,
            odometer: Number(groupedValues.odometer) || undefined,
            entryDate: moment(groupedValues.entryDate).toISOString(),
            regionalId: Number(groupedValues.regionalId) || undefined,
            subject: groupedValues.subject,
			customerComment: groupedValues.customerComment,
            status: 'OPEN',
            phone: groupedValues?.phone,
            partName: groupedValues?.partName,
            diagnose: groupedValues?.diagnose,
            repair: groupedValues?.repair,
            analysisReason: groupedValues?.analysisReason,
            contractNumber: groupedValues.contractNumber,
            vehicleQtd: Number(groupedValues.vehicleQtd) || undefined,
            cancelReason: groupedValues.cancelReason,
            cancelComment: groupedValues.cancelComment,
            requestDetail: groupedValues.requestDetail,
            aeg: groupedValues.aeg,
            subtopic: groupedValues.subtopic,
            partOrComponentName: groupedValues.partOrComponentName,
            partNumber: groupedValues.partNumber,
            problemDescription: groupedValues.problemDescription,
            failureFrequency: groupedValues.failureFrequency,
            problemSteps: groupedValues.problemSteps,
            diagnosticAndResults: groupedValues.diagnosticAndResults,
        };

        setLoading(true)

        const response = await createSupportCall(payload);

        if(files.length && response) {
            const results = await Promise.all(
                Array.from(files).map((file) =>
                uploadFileSupport({ path: response.id, file: [file] })
                )
            );
            
            if (results.some((success) => !success)) {
                Notification.error('Ocorreu um erro ao criar a solicitação. Por favor, tente novamente.');
                setLoading(false);
                return;
            }
        }

        if (response) {
            setFormData(initialFormData);
            setErrors({});

            Notification.success('Solicitação criada com sucesso!');
            handleBackClick();

        } else {
            Notification.error('Ocorreu um erro ao criar a solicitação. Por favor, tente novamente.');
        }

        setLoading(false)
    };

    const subject = formData.subject;

    const getSubjectOptions = () => {
        if(formData.supportType === 'TECHNICAL_SUPPORT') {
            return supportSubjectOptions.map(option => (
                <option key={option.id} value={option.id}>
                    {option.label}
                </option>
            ));
        }
        
        return supportStatusOptions.map(option => (
            <option key={option.id} value={option.id}>
                {option.label}
            </option>
        ));
    }

    const setSubjectValue = () => {
        if(subject === SupportType.ADMINISTRATIVE){
            updateSubtopic(optionsAdministrative[0].id);
        }

        if(subject === SupportType.FINANCIAL){
            updateSubtopic(optionsFinancial[0].id);
        }

        if(subject === SupportType.PORTAL){
            updateSubtopic(optionsVolksTotalPortal[0].id);
        }

        if(subject === SupportType.TECHNICAL){
            updateSubtopic(optionsTechnical[0].id);
        }

        if(
            F2_FORM_SUBJECTS.includes(subject as string) || 
            subject === 'CABIN' ||
            subject === 'EXHAUST_SYSTEM_AND_AFTER_TREATMENT' ||
            subject === 'SOFTWARE_DIESEL_VEHICLE' ||
            subject === 'ENGINE_REPLACEMENT_REQUEST' ||
            subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN' ||
            subject === 'TAS_DIAGNOSIS_TOOLS'
        ) {
            updateSubtopic(optionsTechnical[0].id);
        }
    }

    useEffect(() => {
        setSubjectValue();
        setErrors({});
    }, [subject])

    useEffect(() => {
        if (vehicle?.licensePlate) {
            setFormData(({
                ...formData,
                licensePlate: vehicle?.licensePlate,
            }));
        }
    }, [vehicle?.licensePlate])

    const getThemeSelectOptions = () => {
        if(subject === SupportType.ADMINISTRATIVE){
            return optionsAdministrative.map(option => {
                return (
                    <option value={option.id}>{option.label}</option>
                )
            })
        }

        if(subject === SupportType.FINANCIAL){
            return optionsFinancial.map(option => {
                return (
                    <option value={option.id}>{option.label}</option>
                )
            })
        }

        if(subject === SupportType.PORTAL){
            return optionsVolksTotalPortal.map(option => {
                return (
                    <option value={option.id}>{option.label}</option>
                )
            })
        }

        if(subject === SupportType.TECHNICAL){
            return optionsTechnical.map(option => {
                return (
                    <option value={option.id}>{option.label}</option>
                )
            })
        }

        return ''
    }

    useEffect(() => {
        if(formData.supportType === 'TECHNICAL_SUPPORT') {
            updateSubject(supportSubjectOptions[0].id);
            return;
        }
        
        updateSubject(supportStatusOptions[0].id);
    }, [formData.supportType])

    return (
        <div className='panel panel-default panel-body height-100pct shadow-default margin-top-20' style={{ maxWidth: 1400, margin: '0 auto' }}>
            <header className='display-flex justify-content-between align-items-center border border-bottom-only padding-bottom-25'>
                <h1 className='text-capitalize text-color-darkest text-size-h4'>Nova solicitação</h1>
                <button onClick={handleBackClick} type='button' className='btn btn-muted'>
                    <span className='rioglyph rioglyph-remove margin-0 text-color-darkest' />
                </button>
            </header>
            <main className='padding-top-25 display-flex gap-40'>
                <form onSubmit={handleNewData} className='flex-1-1'>
                    <p className='text-size-16 text-bold'>1. Dados Iniciais</p>
                    <div className='padding-top-20'>
                        <p className='text-size-14 text-color-darker margin-bottom-5'>Tipo de suporte</p>
                        <div className='form-group display-flex'>
                            {role === 'FACTORY' || role === 'TOWER' ? (
                                <div>
                                    <label className='radio' htmlFor='radio1'>
                                        <input
                                            type='radio'
                                            id='radio1'
                                            name='radioGroup'
                                            value='TECHNICAL_SUPPORT'
                                            checked={formData.supportType === 'TECHNICAL_SUPPORT'}
                                            onChange={event => updateSupportType(event.target.value)}
                                        />
                                        <span className='radio-text'>
                                            <span>TAS - Suporte Técnico</span>
                                        </span>
                                    </label>
                                </div>
                            ) : null}
                            <div>
                                <label className='radio' htmlFor='radio2'>
                                    <input
                                        type='radio'
                                        id='radio2'
                                        name='radioGroup'
                                        value='VOLKSTOTAL'
                                        checked={formData.supportType === 'VOLKSTOTAL'}
                                        onChange={event => updateSupportType(event.target.value)}
                                    />
                                    <span className='radio-text'>
                                        <span>Suporte Volkstotal</span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className='padding-top-20'>
                        <span className='text-size-14 text-color-darker'>Assunto</span>
                        <div className='form-group display-flex padding-top-10'>
                            <select
                                className='form-control'
                                value={formData.subject}
                                onChange={event => updateSubject(event.target.value)}
                            >
                                {getSubjectOptions()}
                            </select>
                        </div>
                    </div>

                    {subject !== SupportType.COMMERCIAL && subject !== SupportType.CONTRACT_CANCELLATION && formData.supportType !== "TECHNICAL_SUPPORT" ? (
                        <div className=''>
                            <span className='text-size-14 text-color-darker'>Tema</span>
                            <div className='form-group display-flex padding-top-10'>
                                <select
                                    className='form-control'
                                    value={formData.subtopic}
                                    onChange={event => updateSubtopic(event.target.value)}
                                >
                                    {getThemeSelectOptions()}
                                </select>
                            </div>
                        </div>
                    ) : null}

                    {(
                        subject === 'ADMINISTRATIVE' ||
                        subject === 'FINANCIAL' ||
                        subject === 'PORTAL' ||
                        subject === 'COMMERCIAL'
                    ) ? (
                        <AdministrativeForm 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField} 
                            subject={subject}
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'TECHNICAL' ? (
                        <TechnicalForm 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField} 
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'CONTRACT_CANCELLATION' ? (
                        <CancellationForm 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'CABIN' ? (
                        <TasF1Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {F2_FORM_SUBJECTS.includes(subject as string) ? (
                        <TasF2Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField} 
                            name={loggedUserName}
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'EXHAUST_SYSTEM_AND_AFTER_TREATMENT' ? (
                        <TasF3Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'SOFTWARE_DIESEL_VEHICLE' ? (
                        <TasF4Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'ENGINE_REPLACEMENT_REQUEST' ? (
                        <TasF5Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'SOFTWARE_IDLE_AUTO_SHUTDOWN' ? (
                        <TasF6Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    {subject === 'TAS_DIAGNOSIS_TOOLS' ? (
                        <TasF7Form 
                            formData={formData} 
                            errors={errors} 
                            updateField={updateField}
                            name={loggedUserName} 
                            fetchVehicle={fetchVehicle}
                        />
                    ) : null}

                    <div className="margin-top-20">
                        <SimpleUpload
                           addFileToUpload={(files) => setFiles((prev) => [...prev, ...files])}
                           removeFileToUpload={(fileName) => setFiles((prev) => prev.filter((f) => f.name !== fileName))}
                        />
                    </div>

                    <div className='form-group margin-top-20'>
                        <div className='display-flex justify-content-end'>
                            {!loading && (
                                <>
                                    <button onClick={handleBackClick} type='reset' className='btn btn-default margin-right-10'>
                                        Cancelar
                                    </button>
                                    <button type='submit' className='btn btn-primary'>
                                        Criar suporte
                                    </button>
                                </>
                            )}
                            {loading && <Spinner />}
                        </div>
                    </div>
                </form>
                <div className="min-width-400 max-width-400 height-100pct">
                    <VehicleData data={vehicle} isLoading={isLoadingVehicleFetch} />
                </div>
            </main>
        </div>
    );
};

export default NewSupportCallPage;
